import React, {useCallback, useEffect, useState} from 'react'
import {
  BookingPaymentButton,
  BookingPaymentNoPayment,
  BookingPaymentPayIcon,
  BookingPaymentPaymentSaveInfoContainer
} from './style'
import {Box, Grid, Skeleton, Typography} from '@mui/material';
import BuyerPaymentMethodOption from './BuyerPaymentMethodOption';
import {determinePaymentImage} from "../../../util/PaymentUtils";
import {Account} from '../../../types/Account';
import {verbiage} from './verbiage';
import AddPaymentMethodModalRedesign from "./AddPaymentMethodModalRedesign";
import {requestPaymentMethods} from '../request/payment-method-requests';
import Busy from '../../Busy';
import {withSnackbar, WithSnackbarProps} from "../../hocs/withSnackbar";
import {getErrorMessageForNonStandardAndStandardResponse} from "../../../util/NetworkErrorUtil";

interface PaymentMethod {
  id: string;
  lastFour: string;
  nickName?: string;
  type?: string;
  cardBrand?: string;
  stripePaymentMethodId?: string;
  stripeStatus?: string;
  dwollaStatus?: string;
}

interface BuyerPaymentMethodSelectProps extends WithSnackbarProps {
  account: Account;
  setSelectedPaymentMethod: (id: string) => void;
}

const BuyerPaymentMethodSelect = ({ account, setSelectedPaymentMethod, snackbarShowMessage }: BuyerPaymentMethodSelectProps) => {
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [selectedMethodId, setSelectedMethodId] = useState('');

  const filterAndSetPaymentMethods = useCallback((methods: PaymentMethod[]) => {
    let filtered = methods;
    if (filtered?.length) {
      filtered = filtered.filter((paymentMethod) => {
        if (paymentMethod?.stripePaymentMethodId) {
          return true;
        }
        return paymentMethod?.dwollaStatus !== 'unverified' && paymentMethod?.stripeStatus !== 'unverified';
      });
    }

    if (filtered?.length) {
      setPaymentMethods(filtered);
    } else {
      setPaymentMethods([]);
      snackbarShowMessage(verbiage.noPaymentMethodsError, 'error', 15000);
    }
  }, [snackbarShowMessage]);

  const loadPaymentMethods = useCallback(() => {
    Busy.set(true);
    requestPaymentMethods(
      account.authorityId || account.id,
      (response: PaymentMethod[]) => {
        filterAndSetPaymentMethods(response);
        Busy.set(false);
      },
      (error: unknown) => {
        const appError = getErrorMessageForNonStandardAndStandardResponse(error);
        console.error(appError);
        Busy.set(false);
      },
      true
    )
  }, [account, filterAndSetPaymentMethods]);

  useEffect(() => {
    loadPaymentMethods();
  }, [loadPaymentMethods]);

  const handleSelectPaymentMethod = (value: string) => {
    setSelectedMethodId(value);
    setSelectedPaymentMethod(value);
  }

  const showAddPaymentModal = () => {
    setPaymentModalOpen(true);
  }

  const closeAddPaymentModal = (needsReload?: boolean) => {
    setPaymentModalOpen(false);
    needsReload && loadPaymentMethods();
  }

  return (
    <Box>
      <Grid container justifyContent='space-between' alignItems='center'>
        <Typography variant={'h5'} color={'textPrimary'}>
          {verbiage.payWith}
        </Typography>
        <BookingPaymentButton onClick={showAddPaymentModal} variant='contained' color='secondary'>
          <BookingPaymentPayIcon />
          {verbiage.addPayment}
        </BookingPaymentButton>
      </Grid>
      {
        !paymentMethods.length ?
          <Skeleton variant={'text'} width={'100%'}/>
          : paymentMethods.length > 0 ?
            <BookingPaymentPaymentSaveInfoContainer>
              {
                paymentMethods.map((paymentMethod) => {
                    return (
                      <BuyerPaymentMethodOption
                        key={paymentMethod.id}
                        img={determinePaymentImage(paymentMethod)}
                        cardInfo={`...` + paymentMethod.lastFour + (paymentMethod.nickName ? ` (${paymentMethod.nickName})` : '')}
                        isSelect={selectedMethodId === paymentMethod.id}
                        id={paymentMethod.id}
                        setIsSelect={handleSelectPaymentMethod}
                      />
                    )
                  }
                )}
            </BookingPaymentPaymentSaveInfoContainer>
            :
            <BookingPaymentNoPayment>
              {verbiage.noPaymentMethod}
            </BookingPaymentNoPayment>
      }

      <AddPaymentMethodModalRedesign
        account={account}
        open={paymentModalOpen}
        onClose={closeAddPaymentModal}
      />
    </Box>
  )
}

export default withSnackbar(BuyerPaymentMethodSelect)
